import dayjs from "dayjs";
import {
  ComponentProps,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button } from "@design-system/Button/Button";
import {
  DateRangePicker,
  DateRangePickerRef,
} from "@design-system/DateRangePicker/DateRangePicker";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";

type DateRange = [Date | null, Date | null];

const dateRangeToFormValues = (
  range: DateRange,
): { date_Gte?: string; date_Lte?: string } => {
  const [startDate, endDate] = range;
  return {
    date_Gte: startDate ? dayjs(startDate).format("YYYY-MM-DD") : undefined,
    date_Lte: endDate ? dayjs(endDate).format("YYYY-MM-DD") : undefined,
  };
};

const formValuesToDateRange = (
  date_Gte?: string,
  date_Lte?: string,
): DateRange => {
  return [
    date_Gte ? dayjs(date_Gte).toDate() : null,
    date_Lte ? dayjs(date_Lte).toDate() : null,
  ];
};

export type ApplicationDashboardFilterFormValues = {
  date_Gte?: string | undefined;
  date_Lte?: string | undefined;
};

export type ApplicationDashboardFilterFormRef = {
  getCurrentValues: () => ApplicationDashboardFilterFormValues;
  /** Commit typed drafts; false means invalid/reversed — do not apply or query. */
  applyIfValid: () => boolean;
};

type Props = {
  onFilterChange: SubmitHandler<ApplicationDashboardFilterFormValues>;
  values: ApplicationDashboardFilterFormValues;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
};

export const ApplicationDashboardFilterForm = forwardRef<
  ApplicationDashboardFilterFormRef,
  Props
>(({ onFilterChange, values, errorsAPI }, ref) => {
  const dateRangePickerRef = useRef<DateRangePickerRef>(null);
  const { handleSubmit, setValue, watch, getValues } =
    useForm<ApplicationDashboardFilterFormValues>({
      values: {
        date_Gte: values.date_Gte ?? undefined,
        date_Lte: values.date_Lte ?? undefined,
      },
    });

  const date_Gte = watch("date_Gte");
  const date_Lte = watch("date_Lte");

  const handleDateRangeChange = (range: DateRange) => {
    const { date_Gte, date_Lte } = dateRangeToFormValues(range);
    setValue("date_Gte", date_Gte, { shouldDirty: true });
    setValue("date_Lte", date_Lte, { shouldDirty: true });
  };

  useImperativeHandle(
    ref,
    () => ({
      getCurrentValues: () => getValues(),
      applyIfValid: () => dateRangePickerRef.current?.commit() ?? true,
    }),
    [getValues],
  );

  const handleValidSubmit: SubmitHandler<
    ApplicationDashboardFilterFormValues
  > = () => {
    if (!dateRangePickerRef.current?.commit()) {
      return;
    }
    onFilterChange(getValues());
  };

  return (
    <Form onSubmit={handleSubmit(handleValidSubmit)} errorsAPI={errorsAPI}>
      <FieldGroup label="Activity between">
        <DateRangePicker
          ref={dateRangePickerRef}
          fullWidth
          value={formValuesToDateRange(date_Gte, date_Lte)}
          onChange={handleDateRangeChange}
          placeholder="Select date range"
        />
      </FieldGroup>
      <Button type="submit" style={{ display: "none" }} />
    </Form>
  );
});

ApplicationDashboardFilterForm.displayName = "ApplicationDashboardFilterForm";
